import { Box, Field, HStack, Input, Text, VStack } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { StreetViewImage } from '../../components/common/StreetViewImage'
import pilotHttp from '../pilotHttp'
import { PilotAddressAutocompleteResponse, PilotAddressSuggestion } from '../pilotTypes'

/**
 * Address entry backed by Chimnie's address autocomplete, proxied through
 * GET /api/pilot/{council}/address-autocomplete/ so the Chimnie key never
 * reaches the browser.
 *
 * Drop-in for the pilot's use of AddAddressForm: it emits the same
 * `details` object shape via onAddressSelected so PilotPage and the assess
 * payload stay unchanged. Chimnie returns a UK postal address string with no
 * administrative area, so those Google-only fields are left blank.
 */

export interface ChimnieAddressDetails {
  fullAddress: string
  formattedAddress: string
  shortAddress: string
  postcode: string
  streetNumber: string
  route: string
  locality: string
  city: string
  administrativeArea: string
  country: string
  sublocality: string
  ward: string
  postalTown: string
  adminArea2: string
  adminArea3: string
  imageUrl?: string
  addressProvider: 'chimnie'
}

interface ChimnieAddressFormProps {
  council: string
  onAddressSelected: (details: ChimnieAddressDetails) => void
  onClearApiError?: () => void
  brandColor?: string
}

const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 300

/**
 * Chimnie's first line is "10 High Street" or "Flat 6, Chimnie Court". The
 * backend's structured EPC fallback keys on street number + street name, so
 * split them off the last comma segment when it starts with a number.
 */
const splitStreet = (line1: string): { streetNumber: string; route: string } => {
  const segment = line1.split(',').pop()?.trim() ?? ''
  const match = segment.match(/^(\d+[A-Za-z]?)\s+(.+)$/)
  return match
    ? { streetNumber: match[1], route: match[2] }
    : { streetNumber: '', route: segment }
}

export const detailsFromSuggestion = (s: PilotAddressSuggestion): ChimnieAddressDetails => ({
  fullAddress: s.address,
  formattedAddress: s.address,
  shortAddress: s.line1,
  postcode: s.postcode,
  ...splitStreet(s.line1),
  locality: s.city,
  city: s.city,
  administrativeArea: '',
  country: 'United Kingdom',
  sublocality: '',
  ward: '',
  postalTown: s.city,
  adminArea2: s.county,
  adminArea3: '',
  imageUrl: undefined,
  addressProvider: 'chimnie',
})

/**
 * Chimnie marks matches with <em>…</em>. Render those as coloured spans without
 * touching innerHTML: split on the tags and treat everything else as text.
 */
const renderHighlight = (highlight: string, fallback: string, color: string) => {
  const source = highlight || fallback
  const parts = source.split(/(<em>.*?<\/em>)/g).filter(Boolean)
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^<em>(.*?)<\/em>$/)
        return match
          ? <span key={i} style={{ color, fontWeight: 600 }}>{match[1]}</span>
          : <span key={i}>{part}</span>
      })}
    </>
  )
}

const ChimnieAddressForm = ({
  council,
  onAddressSelected,
  onClearApiError,
  brandColor = '#2f855a',
}: ChimnieAddressFormProps) => {
  const [inputValue, setInputValue] = useState('')
  const [selected, setSelected] = useState<ChimnieAddressDetails | null>(null)
  const [suggestions, setSuggestions] = useState<PilotAddressSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Chimnie groups one user's keystrokes into a billing session; we hand the
  // id back on each request and drop it once an address is chosen.
  const chimnieSessionRef = useRef<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestRequestRef = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const fetchSuggestions = async (query: string) => {
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    const requestId = ++latestRequestRef.current
    setIsSearching(true)
    try {
      const res = await pilotHttp.get<PilotAddressAutocompleteResponse>(
        `/api/pilot/${council}/address-autocomplete/`,
        { params: { q: query.trim(), session: chimnieSessionRef.current ?? undefined } },
      )
      // A slower, older response must not overwrite a newer query's results.
      if (requestId !== latestRequestRef.current) return
      chimnieSessionRef.current = res.data.session ?? chimnieSessionRef.current
      setSuggestions(res.data.suggestions ?? [])
      setShowSuggestions(true)
      setActiveIndex(-1)
      setError(null)
    } catch (err: any) {
      if (requestId !== latestRequestRef.current) return
      setSuggestions([])
      setShowSuggestions(false)
      const code = err?.response?.data?.errorCode
      setError(
        code === 'RATE_LIMITED'
          ? 'Too many searches. Please wait a moment and try again.'
          : 'Address lookup is unavailable right now. Please try again.',
      )
    } finally {
      if (requestId === latestRequestRef.current) setIsSearching(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setSelected(null)
    setError(null)
    if (onClearApiError) onClearApiError()
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(value), DEBOUNCE_MS)
  }

  const selectSuggestion = (suggestion: PilotAddressSuggestion) => {
    const details = detailsFromSuggestion(suggestion)
    if (!details.postcode) {
      setError("We couldn't read a postcode for that address. Please pick another suggestion.")
      return
    }
    latestRequestRef.current++ // discard any in-flight search
    chimnieSessionRef.current = null
    setSelected(details)
    setInputValue(details.fullAddress)
    setSuggestions([])
    setShowSuggestions(false)
    setIsSearching(false)
    setError(null)
    onAddressSelected(details)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (!inputValue.trim()) setError('Please enter an address')
        else if (!selected) setError('Please select an address from the dropdown')
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => Math.max(prev - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0) selectSuggestion(suggestions[activeIndex])
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  return (
    <Box borderRadius="lg" borderColor="gray.200">
      <VStack gap={4} align="stretch">
        <Field.Root>
          <Field.Label>What's the home address?</Field.Label>
          <Box ref={containerRef} position="relative" w="100%">
            <Input
              autoFocus
              autoComplete="off"
              placeholder="123 High Street, London, SW1A 1AA"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              size="lg"
              aria-autocomplete="list"
              aria-expanded={showSuggestions && suggestions.length > 0}
            />
            {showSuggestions && (suggestions.length > 0 || (!isSearching && inputValue.trim().length >= MIN_QUERY_LENGTH)) && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={2000}
                bg="white"
                border="1px solid #e2e8f0"
                borderRadius="8px"
                boxShadow="0 8px 20px rgba(17, 17, 17, 0.08)"
                mt={1}
                p="4px"
                overflow="hidden"
                role="listbox"
              >
                {suggestions.length === 0 && (
                  <Text px="18px" py="12px" fontSize="sm" color="gray.500">
                    No addresses found. Try adding the postcode.
                  </Text>
                )}
                {suggestions.map((s, i) => {
                  const isActive = i === activeIndex
                  return (
                    <Box
                      key={`${s.address}-${i}`}
                      px="18px"
                      py="12px"
                      cursor="pointer"
                      bg={isActive ? '#f7fafc' : 'white'}
                      _hover={{ bg: '#f7fafc' }}
                      role="option"
                      aria-selected={isActive}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        selectSuggestion(s)
                      }}
                      onMouseEnter={() => setActiveIndex(i)}
                    >
                      <HStack gap="20px" align="center">
                        <Box flexShrink={0} mt="2px">
                          <svg width="15" height="20" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.5 0C3.364 0 0 3.364 0 7.5c0 5.625 7.5 12.5 7.5 12.5S15 13.125 15 7.5C15 3.364 11.636 0 7.5 0zm0 10.625A3.125 3.125 0 1 1 7.5 4.375a3.125 3.125 0 0 1 0 6.25z" fill="#9E9E9E"/>
                          </svg>
                        </Box>
                        <Text fontSize="18px" color="#1a202c" lineHeight="1.4" lineClamp={1}>
                          {renderHighlight(s.highlight, s.address, brandColor)}
                        </Text>
                      </HStack>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Field.Root>

        {selected && (
          <Box
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
            display="flex"
            alignItems="center"
          >
            <StreetViewImage
              imageUrl={selected.imageUrl}
              address={selected.fullAddress}
              svSize="300x200"
              alt={`Street view of ${selected.fullAddress}`}
              h="72px"
              w="96px"
              flexShrink={0}
              objectFit="cover"
              showLabel={false}
            />
            <Box flex={1} px={3} py={2}>
              <Text fontWeight="semibold" fontSize="sm" color="gray.900" lineHeight="1.3">
                {selected.shortAddress}, {selected.postcode}
              </Text>
              {selected.city && (
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  {selected.city}{selected.adminArea2 ? `, ${selected.adminArea2}` : ''}
                </Text>
              )}
            </Box>
          </Box>
        )}

        {error && (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        )}
      </VStack>
    </Box>
  )
}

export default ChimnieAddressForm
